import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  FileText,
  DollarSign,
  Edit,
  Trash2,
  ShoppingCart,
  History,
} from 'lucide-react';
import { RowActionsMenu, RowAction, AuditHistoryModal } from '../../../../../components/StandardPage';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import CloseSaleFashionModal from '../CloseSaleFashionModal';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ModalContext from '../../../../../contexts/ModalContext';
import axios from '../../../../../config/axios';
import FashionApiService from '../../../../../services/FashionApiService';

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`

interface FashionOrder {
  id: number;
  saleStatus?: string | null;
  status?: boolean | null;
  client?: string | null;
}

interface ActionsDropdownProps {
  order: FashionOrder;
  onDelete: () => void;
  onUpdate: () => void;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ order, onDelete, onUpdate }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseSaleModal, setShowCloseSaleModal] = useState(false);
  const [showRevertSaleModal, setShowRevertSaleModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const { setModalActive, setModalSection, setSelectedFashionOrderId, setSelectedSteelOrder } = useContext(ModalContext);

  const handleAction = (action: string) => {
    switch (action) {
      case 'view':
        setSelectedFashionOrderId(order.id);
        setModalSection('viewFashionOrder');
        setModalActive(true);
        break;
      case 'edit':
        navigate(`/panel/fashion/edit/${order.id}`);
        break;
      case 'close-sale':
        setShowCloseSaleModal(true);
        break;
      case 'revert-sale':
        setShowRevertSaleModal(true);
        break;
      case 'generate-pdf':
        setSelectedFashionOrderId(order.id);
        setModalSection('generateFashionOrderPdf');
        setModalActive(true);
        break;
      case 'invoice':
        setSelectedSteelOrder(order);
        setModalSection('generateOrderInvoice');
        setModalActive(true);
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      case 'audit':
        setShowAuditModal(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      await axios.delete(
        `${ORDERS_URL}/order/${order.id}`
      );
      setFeedback({ message: `Pedido ${order.id} excluído com sucesso!`, isOpen: true, statusOk: true });
      setShowDeleteModal(false);
      onDelete();
    } catch (error: any) {
      console.error('Erro ao excluir pedido:', error);
      setFeedback({ message: 'Erro ao excluir pedido. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmCloseSale = async () => {
    try {
      setIsLoading(true);
      await axios.patch(
        `${ORDERS_URL}/order/${order.id}`,
        { saleStatus: 'Fechado' }
      );

      let opError: any = null;
      try {
        const response = await FashionApiService.generateProductionOrdersFromOrder(order.id);
        const createdOps = response.data || [];
        for (const op of createdOps) {
          if (op.id) {
            await axios.post(
              `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-production-internal/create-sales-stock-items`,
              { serviceId: op.id, tenantId: op.tenantId }
            );
          }
        }
      } catch (err: any) {
        opError = err;
        console.error('Erro ao gerar ordens de produção:', err);
      }

      if (opError) {
        const rawOpMsg = opError?.response?.data?.message;
        const opMsg = (Array.isArray(rawOpMsg) ? rawOpMsg.join(', ') : rawOpMsg) || opError?.message || 'erro desconhecido';
        setFeedback({
          message: `Venda do pedido ${order.id} fechada, mas a geração das ordens de produção falhou: ${opMsg}`,
          isOpen: true,
          statusOk: false,
        });
      } else {
        setFeedback({ message: `Venda do pedido ${order.id} fechada com sucesso!`, isOpen: true, statusOk: true });
      }
      setShowCloseSaleModal(false);
      onUpdate();
    } catch (error: any) {
      console.error('Erro ao fechar venda:', error);
      const rawMsg = error?.response?.data?.message;
      const apiMsg = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
      setFeedback({ message: apiMsg || 'Erro ao fechar venda. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSale = async () => {
    try {
      setIsLoading(true);
      await axios.patch(
        `${ORDERS_URL}/order/${order.id}`,
        { saleStatus: 'Cancelado' }
      );
      setFeedback({ message: `Venda do pedido ${order.id} cancelada!`, isOpen: true, statusOk: true });
      setShowCloseSaleModal(false);
      onUpdate();
    } catch (error: any) {
      console.error('Erro ao cancelar venda:', error);
      const rawMsg = error?.response?.data?.message;
      const apiMsg = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
      setFeedback({ message: apiMsg || 'Erro ao cancelar venda. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRevertSale = async () => {
    try {
      setIsLoading(true);
      if (order.saleStatus === 'Cancelado') {
        await axios.patch(
          `${ORDERS_URL}/order/${order.id}`,
          { saleStatus: 'Em Orçamento' }
        );
      } else {
        await FashionApiService.revertFashionSale(order.id);
      }
      setFeedback({ message: `Venda do pedido ${order.id} revertida com sucesso!`, isOpen: true, statusOk: true });
      setShowRevertSaleModal(false);
      onUpdate();
    } catch (error: any) {
      console.error('Erro ao reverter venda:', error);
      setFeedback({ message: error?.response?.data?.message || 'Erro ao reverter venda. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelCloseSale = () => {
    setShowCloseSaleModal(false);
  };

  const canCloseSale = order.saleStatus === 'Em Orçamento' || order.saleStatus === 'Em orçamento' || order.saleStatus === 'Em andamento';
  const canRevertSale = order.saleStatus === 'Fechado' || order.saleStatus === 'Cancelado';

  const actions: RowAction[] = [
    ...(canCloseSale
      ? [{ key: 'close-sale', label: 'Fechar venda', icon: <ShoppingCart size={16} />, onClick: () => handleAction('close-sale') }]
      : []),
    ...(canRevertSale
      ? [{ key: 'revert-sale', label: 'Reverter venda', icon: <ShoppingCart size={16} />, onClick: () => handleAction('revert-sale') }]
      : []),
    { key: 'generate-pdf', label: 'Gerar PDF', icon: <FileText size={16} />, onClick: () => handleAction('generate-pdf') },
    /** O fashion fecha a venda por `saleStatus` — `status` é o tri-estado do aço e aqui nunca é gravado.
     *  Só venda fechada fatura: `canRevertSale` não serve, porque também cobre 'Cancelado'. */
    ...(order.saleStatus === 'Fechado'
      ? [{ key: 'invoice', label: 'Faturar', icon: <DollarSign size={16} />, onClick: () => handleAction('invoice') }]
      : []),
    { key: 'view', label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleAction('view') },
    { key: 'edit', label: 'Editar', icon: <Edit size={16} />, onClick: () => handleAction('edit') },
    { key: 'audit', label: 'Auditoria', icon: <History size={16} color="#2563eb" />, onClick: () => handleAction('audit') },
    { key: 'delete', label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleAction('delete') },
  ];

  return (
    <>
      <RowActionsMenu actions={actions} />
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Pedido"
        message={`Tem certeza que deseja excluir o pedido ${order.id}? Esta ação não pode ser desfeita.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: handleCancelDelete,
            variant: 'outline'
          },
          {
            label: isLoading ? 'Excluindo...' : 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
      <ConfirmationModal
        isOpen={showRevertSaleModal}
        title="Reverter Venda"
        message={`Tem certeza que deseja reverter a venda do pedido ${order.id}? Todas as ordens de produção e itens de estoque de vendas correspondentes serão excluídos permanentemente.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setShowRevertSaleModal(false),
            variant: 'outline'
          },
          {
            label: isLoading ? 'Revertendo...' : 'Reverter',
            onClick: handleConfirmRevertSale,
            variant: 'danger'
          }
        ]}
      />
      <CloseSaleFashionModal
        isOpen={showCloseSaleModal}
        onClose={handleCancelCloseSale}
        onConfirm={handleConfirmCloseSale}
        onCancelSale={handleCancelSale}
        orderId={order.id}
        isLoading={isLoading}
      />
      {showAuditModal && (
        <AuditHistoryModal
          tableName="Order"
          recordId={order.id}
          recordTitle={`Pedido ${order.id}`}
          onClose={() => setShowAuditModal(false)}
        />
      )}
    </>
  );
};

export default ActionsDropdown;
